import { useState } from 'react';
import { format, parse } from 'date-fns';
import { useConfig } from '../context/ConfigContext';
import { getCourtRate, parseTimeSlotStartHour, formatTimeSlotRange, getCourtLabel, calculateReservationFee } from '../utils/bookingUtils';
import { formatPackageHour } from '../utils/packageUtils';
import EquipmentRentalModal from './EquipmentRentalModal';
import BookingTotalRow from './BookingTotalRow';
import '../css/UserInfoForm.css';
import { formatMoney } from '../utils/currencyUtil';
import { getPartialPaymentPercent, isPartialPaymentEnabled } from '../utils/partialPaymentUtil';
import { GUEST_EMAIL_FIELD_HINT, GUEST_EMAIL_FIELD_OPTIONAL_HINT } from '../constants/bookingConst';

// 3. User Information Form Component — styled after jassen's "Booking details" card.
// `selectedDates` is `{ [dateStr]: { [timeSlot]: court[] } }` — the guest
// can now accumulate court/time selections across multiple dates (see
// TimeSlotSelection.jsx), so this renders one booking-box card per date
// instead of assuming a single selectedDate/selectedSlots pair. Uses the
// same dateGroups pattern as ConfirmationScreen.jsx's recap.
// `packageBooking` is set only when the guest came through the event-package
// flow (see App.jsx). It replaces the per-hour court breakdown with a single
// package line: those slots are sold as one block, so listing them at hourly
// rates would show numbers that don't add up to what's being charged.
function UserInfoForm({ onSubmit, formData, setFormData, errors, selectedDates, sportId = null, onBack, packageBooking = null }) {
  const { config } = useConfig();
  // Continue opens the "Need equipments?" rental step first (see
  // EquipmentRentalModal) whenever this sport has anything to rent, so the guest
  // can add paddle/ball/machine rentals before the booking is created.
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  // Only promise an email confirmation when the club actually sends them
  // (`=== true`, matching emailServiceUtil's check, so unset counts as off).
  const emailHint = config?.emailSendingEnabled === true ? GUEST_EMAIL_FIELD_HINT : GUEST_EMAIL_FIELD_OPTIONAL_HINT;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Equipment/add-ons configured for this sport (Config -> Equipment tags
  // each item with a sportId). Items the club owns none of are excluded as
  // well as absent ones — an item with inventoryTotal 0 can never be
  // rented, so listing it only to show "Fully booked" is noise. A club
  // left with nothing rentable skips the step entirely (see handleSubmit).
  const sportEquipment = (config?.equipment || []).filter(item => (
    (!sportId || item.sportId === sportId) && (Number(item.inventoryTotal) || 0) > 0
  ));
  // An event package is a whole-venue hire sold at one all-in price, with
  // whatever it comes with listed in its own inclusions — so the per-item
  // rental step doesn't apply to it and is skipped rather than shown with an
  // "Equipment rental total ₱0" the guest can only dismiss.
  const hasEquipment = sportEquipment.length > 0 && !packageBooking;

  const handleSubmit = (e) => {
    e.preventDefault();
    // Skip the rental step entirely when there's nothing to rent, rather
    // than showing an empty modal the guest can only dismiss.
    if (!hasEquipment) {
      onSubmit(null);
      return;
    }
    setShowEquipmentModal(true);
  };

  // Guest picked (or skipped) equipment — stash the selection on formData
  // (so it's visible if they navigate back) and hand it straight to
  // onSubmit as an argument rather than relying on the formData update
  // above having landed yet: setFormData + onSubmit fire back to back on
  // the same click, and since state updates aren't applied synchronously,
  // App.jsx's handleFormSubmit would otherwise still see the pre-rental
  // formData. onSubmit forwards this value on to createPendingBooking,
  // which folds equipmentRental.total into the payment amount and saves
  // the breakdown on the booking record.
  const handleEquipmentConfirm = (equipmentRental) => {
    setFormData(prev => ({ ...prev, equipmentRental }));
    setShowEquipmentModal(false);
    onSubmit(equipmentRental);
  };

  // One card per selected date, chronologically sorted — jassen groups each
  // date's booking summary by court (then time), but our selection state is
  // keyed by date -> timeSlot -> courts[], so invert it per date. Mirrors
  // ConfirmationScreen.jsx's dateGroups rendering so the recap here looks
  // identical to what the guest sees again on the confirmation screen.
  const dateGroups = Object.keys(selectedDates || {}).sort().map(dateStr => {
    const slots = selectedDates[dateStr] || {};
    const courtGroups = {};
    Object.entries(slots).forEach(([timeSlot, courts]) => {
      (courts || []).forEach(court => {
        if (!courtGroups[court]) courtGroups[court] = [];
        // dateStr scopes the lookup to the right weekday — rate bands can be
        // set per day (see getCourtRate in bookingUtils.js).
        courtGroups[court].push({ timeSlot, price: getCourtRate(config, court, timeSlot, null, dateStr) });
      });
    });
    const sortedCourts = Object.keys(courtGroups).map(Number).sort((a, b) => a - b);
    sortedCourts.forEach(court => {
      courtGroups[court].sort((a, b) => parseTimeSlotStartHour(a.timeSlot) - parseTimeSlotStartHour(b.timeSlot));
    });
    return { dateStr, sortedCourts, courtGroups };
  });
  const courtTotal = dateGroups.reduce(
    (sum, group) => sum + group.sortedCourts.reduce(
      (s, court) => s + group.courtGroups[court].reduce((s2, row) => s2 + row.price, 0),
      0
    ),
    0
  );
  // Computed from the same helper App.jsx charges with (see
  // createPendingBooking), against the same `selectedDates` — so the number
  // shown here is the number the guest is about to be billed, flat or per
  // hour. Rolled into Total below so this screen's total matches the one on
  // the checkout screen rather than jumping up by the fee at the last step.
  // Equipment is deliberately absent from both: it's picked after this
  // screen, in the "Need equipments?" modal.
  // An event package pays its own all-in price with no surcharge on top, so
  // the fee line disappears entirely for one — see calculateReservationFee.
  const bookingFee = calculateReservationFee(config, selectedDates, packageBooking);
  const total = (packageBooking ? packageBooking.price : courtTotal) + bookingFee;

  // Partial payment (Config -> Pricing -> "Partial Payment Allowed (%)"):
  // offered only when the club set a percentage above zero. The choice lives
  // on formData so it survives going back from the next step, and is carried
  // into the checkout tab with the booking (see App.jsx).
  const showPartialPayment = isPartialPaymentEnabled(config) && total > 0;
  const partialPercent = getPartialPaymentPercent(config);
  const handlePartialPaymentChange = (checked) => {
    setFormData(prev => ({ ...prev, partialPayment: checked }));
  };

  return (
    <div className="user-info-form">
      <div className="uif-card">
        <div className="uif-head">
          <h2 className="uif-title">Booking details</h2>
          {onBack && (
            <button type="button" className="uif-close" onClick={onBack} aria-label="Back to date &amp; time">&times;</button>
          )}
        </div>

        {packageBooking ? (
          <div className="uif-booking-box">
            <h3>{format(parse(packageBooking.dateStr, 'yyyy-MM-dd', new Date()), 'EEEE, MMMM d, yyyy')}</h3>
            <div className="uif-court-lbl">{packageBooking.packageName}</div>
            <div className="uif-row">
              <span className="uif-time">
                {formatPackageHour(packageBooking.startHour)} – {formatPackageHour(packageBooking.startHour + packageBooking.hours)}
                {' · '}{packageBooking.hours} hr{packageBooking.hours === 1 ? '' : 's'}
              </span>
              <span className="uif-fee">{formatMoney(packageBooking.price, config)}</span>
            </div>
            <div className="uif-row">
              <span className="uif-time">
                {packageBooking.courts.map(c => getCourtLabel(config, c)).join(', ')}
              </span>
            </div>
          </div>
        ) : dateGroups.map(group => group.sortedCourts.length > 0 && (
          <div className="uif-booking-box" key={group.dateStr}>
            <h3>{format(parse(group.dateStr, 'yyyy-MM-dd', new Date()), 'EEEE, MMMM d, yyyy')}</h3>
            {group.sortedCourts.map(court => (
              <div key={court}>
                <div className="uif-court-lbl">{getCourtLabel(config, court)}</div>
                {group.courtGroups[court].map(row => (
                  <div className="uif-row" key={row.timeSlot}>
                    <span className="uif-time">{formatTimeSlotRange(row.timeSlot)}</span>
                    <span className="uif-fee">{formatMoney(row.price, config)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}

        {bookingFee > 0 && (
          <div className="uif-fee-row">
            <span className="uif-time">Processing fee</span>
            <span className="uif-fee">{formatMoney(bookingFee, config)}</span>
          </div>
        )}

        <BookingTotalRow
          idPrefix="uif"
          total={total}
          config={config}
          showPartialOption={showPartialPayment}
          partialChecked={Boolean(formData.partialPayment)}
          onPartialChange={handlePartialPaymentChange}
          partialPercent={partialPercent}
        />

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="fullName">Full Name <span className="uif-req">*</span></label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Juan dela Cruz"
              className={errors.fullName ? 'error' : ''}
            />
            {errors.fullName && <span className="error-message">{errors.fullName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address <span className="uif-field-hint">({emailHint})</span></label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="juandelacruz@email.com"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Contact Number <span className="uif-req">*</span></label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="0917..."
              className={errors.phone ? 'error' : ''}
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <p className="uif-consent">
            By submitting this booking, you consent to the collection and use of your information as described in
            our <span className="uif-pp">Privacy Policy</span>.
          </p>

          <button type="submit" className="uif-continue">Continue</button>
        </form>
      </div>

      <EquipmentRentalModal
        isOpen={showEquipmentModal && hasEquipment}
        onClose={() => setShowEquipmentModal(false)}
        onConfirm={handleEquipmentConfirm}
        selectedDates={selectedDates}
        sportId={sportId}
      />
    </div>
  );
}

export default UserInfoForm;
